// Copyright (c) 2026 aiApas LLC. All Rights Reserved.
// GuardRok — Proprietary and Confidential. Patent Pending.

// /founding — Founding Member Agreement
// Must be completed and signed before founding-price Stripe checkout is unlocked.
// Agreement is saved to founding_agreements table with timestamp + IP.

import { useState, useEffect } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { useAuth } from '../context/AuthContext'
import { GuardRokIcon } from '../components/GuardRokLogo'
import { Shield, CheckCircle, ArrowRight, Lock } from 'lucide-react'
import { SUPABASE_URL, SUPABASE_ANON_KEY } from '../lib/config'

const PLAN_LABELS: Record<string, { name: string; price: string; seats: string }> = {
  starter: { name: 'Starter', price: '$99/mo', seats: '5–10 seats' },
  professional: { name: 'Professional', price: '$199/mo', seats: '11–25 seats' },
  business: { name: 'Business', price: '$399/mo', seats: '26–50 seats' },
  enterprise: { name: 'Enterprise', price: '$699/mo', seats: '51–100 seats' },
}

export default function Founding() {
  const { session, profile } = useAuth()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const plan = searchParams.get('plan') ?? 'professional'
  const planInfo = PLAN_LABELS[plan] ?? PLAN_LABELS.professional

  const [fullName, setFullName] = useState('')
  const [firmName, setFirmName] = useState('')
  const [role, setRole] = useState('')
  const [reviewConsent, setReviewConsent] = useState<'full_name' | 'anonymous' | 'none'>(
    'anonymous'
  )
  const [agreesReview, setAgreesReview] = useState(false)
  const [agreesMfc, setAgreesMfc] = useState(false)
  const [agreesTerms, setAgreesTerms] = useState(false)
  const [signature, setSignature] = useState('')

  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [alreadySigned, setAlreadySigned] = useState(false)

  // Check if already signed
  useEffect(() => {
    if (!session?.user?.id) return
    supabase
      .from('founding_agreements')
      .select('id')
      .eq('user_id', session.user.id)
      .eq('plan', plan)
      .maybeSingle()
      .then(({ data }) => {
        if (data) setAlreadySigned(true)
      })
  }, [session, plan])

  const canSubmit =
    fullName.trim().length > 1 &&
    firmName.trim().length > 1 &&
    role.trim().length > 1 &&
    agreesReview &&
    agreesMfc &&
    agreesTerms &&
    signature.trim().toLowerCase() === fullName.trim().toLowerCase()

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!canSubmit || !session) return
    setSubmitting(true)
    setError(null)

    try {
      const { error: insertErr } = await supabase.from('founding_agreements').insert({
        user_id: session.user.id,
        org_id: (profile as any)?.org_id ?? null,
        email: session.user.email ?? '',
        full_name: fullName.trim(),
        firm_name: firmName.trim(),
        role: role.trim(),
        plan,
        agrees_to_review: agreesReview,
        review_consent: reviewConsent,
        agrees_to_mfc: agreesMfc,
        agrees_to_terms: agreesTerms,
        agreement_version: 'v1.0',
      })

      if (insertErr) throw new Error(insertErr.message)

      // Now proceed to Stripe checkout
      const res = await fetch(`${SUPABASE_URL}/functions/v1/stripe-checkout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          apikey: SUPABASE_ANON_KEY,
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({
          plan,
          org_id: (profile as any)?.org_id ?? '',
          email: session.user.email ?? '',
        }),
      })
      const data = await res.json()
      if (data.url) {
        window.location.href = data.url
      } else {
        throw new Error(data.error ?? 'Checkout failed — please try again.')
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong.')
      setSubmitting(false)
    }
  }

  // Not signed in — redirect to signup
  if (!session) {
    return (
      <div
        style={{
          minHeight: '100vh',
          background: '#030712',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontFamily: 'system-ui',
        }}
      >
        <div
          style={{
            background: '#080f1a',
            border: '1px solid #1e293b',
            borderRadius: 16,
            padding: 32,
            maxWidth: 400,
            textAlign: 'center',
          }}
        >
          <Shield size={32} color="#E63946" style={{ margin: '0 auto 16px' }} />
          <h2 style={{ color: '#f1f5f9', fontWeight: 800, marginBottom: 10 }}>Sign in first</h2>
          <p style={{ color: '#64748b', fontSize: 14, marginBottom: 20 }}>
            Create an account or sign in to access founding member pricing.
          </p>
          <Link
            to={`/signup?plan=${plan}`}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 8,
              background: '#E63946',
              color: '#fff',
              fontWeight: 700,
              fontSize: 14,
              padding: '11px 22px',
              borderRadius: 8,
              textDecoration: 'none',
            }}
          >
            Create account <ArrowRight size={14} />
          </Link>
        </div>
      </div>
    )
  }

  // Already signed — send straight to checkout
  if (alreadySigned) {
    return (
      <div
        style={{
          minHeight: '100vh',
          background: '#030712',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontFamily: 'system-ui',
        }}
      >
        <div
          style={{
            background: '#080f1a',
            border: '1px solid #22c55e',
            borderRadius: 16,
            padding: 32,
            maxWidth: 400,
            textAlign: 'center',
          }}
        >
          <CheckCircle size={32} color="#22c55e" style={{ margin: '0 auto 16px' }} />
          <h2 style={{ color: '#f1f5f9', fontWeight: 800, marginBottom: 10 }}>
            Agreement already on file
          </h2>
          <p style={{ color: '#64748b', fontSize: 14, marginBottom: 20 }}>
            Your founding member agreement for the {planInfo.name} plan is signed. Proceed to
            checkout.
          </p>
          <button
            onClick={async () => {
              setSubmitting(true)
              const res = await fetch(`${SUPABASE_URL}/functions/v1/stripe-checkout`, {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  apikey: SUPABASE_ANON_KEY,
                  Authorization: `Bearer ${session.access_token}`,
                },
                body: JSON.stringify({
                  plan,
                  org_id: (profile as any)?.org_id ?? '',
                  email: session.user.email ?? '',
                }),
              })
              const data = await res.json()
              if (data.url) window.location.href = data.url
              setSubmitting(false)
            }}
            disabled={submitting}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 8,
              background: '#22c55e',
              color: '#000',
              fontWeight: 700,
              fontSize: 14,
              padding: '11px 22px',
              borderRadius: 8,
              border: 'none',
              cursor: 'pointer',
            }}
          >
            {submitting ? (
              'Redirecting…'
            ) : (
              <>
                Continue to checkout <ArrowRight size={14} />
              </>
            )}
          </button>
        </div>
      </div>
    )
  }

  return (
    <div
      style={{
        minHeight: '100vh',
        background: '#030712',
        color: '#fff',
        fontFamily: 'system-ui,sans-serif',
      }}
    >
      {/* Nav */}
      <nav
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '20px 48px',
          borderBottom: '1px solid #0f172a',
          background: 'rgba(3,9,18,0.95)',
          backdropFilter: 'blur(12px)',
          position: 'sticky',
          top: 0,
          zIndex: 100,
        }}
      >
        <Link
          to="/"
          style={{ display: 'flex', alignItems: 'center', gap: 12, textDecoration: 'none' }}
        >
          <GuardRokIcon size={32} />
          <span style={{ fontWeight: 800, fontSize: 16, color: '#fff' }}>
            Guard<span style={{ color: '#E63946' }}>Rok</span>
            <sup style={{ fontSize: 8, color: '#6b7280' }}>™</sup>
          </span>
        </Link>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <Lock size={12} color="#4b5563" />
          <span style={{ fontSize: 12, color: '#4b5563' }}>Secure founding agreement</span>
        </div>
      </nav>

      <div style={{ maxWidth: 680, margin: '0 auto', padding: '48px 24px 80px' }}>
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: 40 }}>
          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 8,
              background: 'rgba(230,57,70,0.1)',
              border: '1px solid rgba(230,57,70,0.25)',
              borderRadius: 20,
              padding: '5px 16px',
              marginBottom: 18,
            }}
          >
            <span
              style={{
                fontSize: 11,
                fontWeight: 700,
                color: '#E63946',
                letterSpacing: '1px',
                textTransform: 'uppercase',
              }}
            >
              Founding Member Agreement
            </span>
          </div>
          <h1 style={{ fontSize: 30, fontWeight: 900, letterSpacing: '-1px', marginBottom: 12 }}>
            Lock in your founding rate
          </h1>
          <p
            style={{
              color: '#94a3b8',
              fontSize: 15,
              lineHeight: 1.7,
              maxWidth: 500,
              margin: '0 auto',
            }}
          >
            Complete this agreement to unlock founding member pricing on the{' '}
            <strong style={{ color: '#f1f5f9' }}>{planInfo.name}</strong> plan —{' '}
            <strong style={{ color: '#22c55e' }}>{planInfo.price}</strong> for {planInfo.seats}.
          </p>
        </div>

        {/* What you get */}
        <div
          style={{
            background: '#080f1a',
            border: '1px solid #1e293b',
            borderRadius: 12,
            padding: '20px 24px',
            marginBottom: 32,
          }}
        >
          <div
            style={{
              fontSize: 12,
              fontWeight: 700,
              color: '#475569',
              letterSpacing: '1px',
              textTransform: 'uppercase',
              marginBottom: 14,
            }}
          >
            Founding member benefits
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {[
              '1-year contract at founding rate',
              '1 bonus year at same rate',
              'Most Favored Customer — always pay less than any new customer',
              'All features added to your plan tier included at no extra charge',
              'Direct founder access — not a support queue',
              'Beta feature access — first to test new features',
            ].map(b => (
              <div key={b} style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                <CheckCircle size={14} color="#22c55e" style={{ flexShrink: 0, marginTop: 2 }} />
                <span style={{ fontSize: 13, color: '#cbd5e1' }}>{b}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit}>
          <div
            style={{
              background: '#080f1a',
              border: '1px solid #1e293b',
              borderRadius: 12,
              padding: '24px',
              marginBottom: 24,
            }}
          >
            <div
              style={{
                fontSize: 12,
                fontWeight: 700,
                color: '#475569',
                letterSpacing: '1px',
                textTransform: 'uppercase',
                marginBottom: 18,
              }}
            >
              Your details
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              {[
                {
                  label: 'Full name',
                  value: fullName,
                  set: setFullName,
                  placeholder: 'Jane Smith',
                  required: true,
                },
                {
                  label: 'Firm / company name',
                  value: firmName,
                  set: setFirmName,
                  placeholder: 'Smith & Associates LLP',
                  required: true,
                },
                {
                  label: 'Your role',
                  value: role,
                  set: setRole,
                  placeholder: 'Managing Partner / Compliance Officer / IT Director',
                  required: true,
                },
              ].map(field => (
                <div key={field.label}>
                  <label
                    style={{
                      fontSize: 12,
                      fontWeight: 600,
                      color: '#94a3b8',
                      display: 'block',
                      marginBottom: 6,
                    }}
                  >
                    {field.label}
                  </label>
                  <input
                    type="text"
                    value={field.value}
                    onChange={e => field.set(e.target.value)}
                    placeholder={field.placeholder}
                    required={field.required}
                    style={{
                      width: '100%',
                      background: '#0f172a',
                      border: '1px solid #374151',
                      borderRadius: 8,
                      padding: '10px 14px',
                      color: '#f1f5f9',
                      fontSize: 14,
                      outline: 'none',
                      boxSizing: 'border-box',
                    }}
                  />
                </div>
              ))}
            </div>
          </div>

          {/* Consent checkboxes */}
          <div
            style={{
              background: '#080f1a',
              border: '1px solid #1e293b',
              borderRadius: 12,
              padding: '24px',
              marginBottom: 24,
            }}
          >
            <div
              style={{
                fontSize: 12,
                fontWeight: 700,
                color: '#475569',
                letterSpacing: '1px',
                textTransform: 'uppercase',
                marginBottom: 18,
              }}
            >
              Agreements & consent
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              {/* Review obligation */}
              <label
                style={{ display: 'flex', alignItems: 'flex-start', gap: 12, cursor: 'pointer' }}
              >
                <input
                  type="checkbox"
                  checked={agreesReview}
                  onChange={e => setAgreesReview(e.target.checked)}
                  style={{
                    marginTop: 3,
                    flexShrink: 0,
                    accentColor: '#22c55e',
                    width: 16,
                    height: 16,
                  }}
                />
                <span style={{ fontSize: 13, color: '#cbd5e1', lineHeight: 1.6 }}>
                  <strong>I agree to submit a product review within 30 days</strong> of activation.
                  GuardRok will send a 3-question form to my email. Completing this review is a
                  condition of founding member pricing.
                </span>
              </label>

              {/* Publish consent */}
              <div style={{ paddingLeft: 28 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: '#6b7280', marginBottom: 10 }}>
                  If GuardRok requests to publish my review, publish it as:
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {[
                    { value: 'full_name', label: 'My full name and firm name' },
                    {
                      value: 'anonymous',
                      label: 'My role and firm type only (e.g. "Managing Partner, AmLaw 200 Firm")',
                    },
                    { value: 'none', label: 'Do not publish — I prefer to keep it private' },
                  ].map(opt => (
                    <label
                      key={opt.value}
                      style={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: 8,
                        cursor: 'pointer',
                      }}
                    >
                      <input
                        type="radio"
                        name="review_consent"
                        value={opt.value}
                        checked={reviewConsent === opt.value}
                        onChange={() => setReviewConsent(opt.value as any)}
                        style={{ marginTop: 2, flexShrink: 0, accentColor: '#22c55e' }}
                      />
                      <span style={{ fontSize: 13, color: '#94a3b8' }}>{opt.label}</span>
                    </label>
                  ))}
                </div>
                <p style={{ fontSize: 11, color: '#475569', marginTop: 10, lineHeight: 1.6 }}>
                  Founding pricing applies regardless of your publishing choice. You may change your
                  publishing preference at any time by emailing hello@guardrok.com.
                </p>
              </div>

              {/* MFC */}
              <label
                style={{ display: 'flex', alignItems: 'flex-start', gap: 12, cursor: 'pointer' }}
              >
                <input
                  type="checkbox"
                  checked={agreesMfc}
                  onChange={e => setAgreesMfc(e.target.checked)}
                  style={{
                    marginTop: 3,
                    flexShrink: 0,
                    accentColor: '#22c55e',
                    width: 16,
                    height: 16,
                  }}
                />
                <span style={{ fontSize: 13, color: '#cbd5e1', lineHeight: 1.6 }}>
                  <strong>I understand the Most Favored Customer guarantee</strong> — I will never
                  pay more than any new customer on the same plan tier, for as long as I remain an
                  active customer.
                </span>
              </label>

              {/* Terms */}
              <label
                style={{ display: 'flex', alignItems: 'flex-start', gap: 12, cursor: 'pointer' }}
              >
                <input
                  type="checkbox"
                  checked={agreesTerms}
                  onChange={e => setAgreesTerms(e.target.checked)}
                  style={{
                    marginTop: 3,
                    flexShrink: 0,
                    accentColor: '#22c55e',
                    width: 16,
                    height: 16,
                  }}
                />
                <span style={{ fontSize: 13, color: '#cbd5e1', lineHeight: 1.6 }}>
                  I have read and agree to the{' '}
                  <Link to="/terms" target="_blank" style={{ color: '#94a3b8' }}>
                    Terms of Service
                  </Link>{' '}
                  and{' '}
                  <Link to="/privacy" target="_blank" style={{ color: '#94a3b8' }}>
                    Privacy Policy
                  </Link>
                  . I understand this is a 1-year contract at the founding rate, followed by a
                  1-year bonus at the same rate. After year 2, standard pricing applies.
                </span>
              </label>
            </div>
          </div>

          {/* Digital signature */}
          <div
            style={{
              background: '#080f1a',
              border: '1px solid #1e293b',
              borderRadius: 12,
              padding: '24px',
              marginBottom: 24,
            }}
          >
            <div
              style={{
                fontSize: 12,
                fontWeight: 700,
                color: '#475569',
                letterSpacing: '1px',
                textTransform: 'uppercase',
                marginBottom: 8,
              }}
            >
              Digital signature
            </div>
            <p style={{ fontSize: 13, color: '#64748b', marginBottom: 14, lineHeight: 1.6 }}>
              Type your full name exactly as entered above to sign this agreement. Your name, IP
              address, and timestamp will be recorded.
            </p>
            <input
              type="text"
              value={signature}
              onChange={e => setSignature(e.target.value)}
              placeholder="Type your full name to sign"
              style={{
                width: '100%',
                background: '#0f172a',
                border: `1px solid ${signature && signature.trim().toLowerCase() === fullName.trim().toLowerCase() ? '#22c55e' : '#374151'}`,
                borderRadius: 8,
                padding: '12px 16px',
                color: '#f1f5f9',
                fontSize: 15,
                fontStyle: 'italic',
                outline: 'none',
                boxSizing: 'border-box',
              }}
            />
            {signature && signature.trim().toLowerCase() !== fullName.trim().toLowerCase() && (
              <p style={{ fontSize: 12, color: '#E63946', marginTop: 6 }}>
                Must match the full name entered above exactly
              </p>
            )}
            {signature && signature.trim().toLowerCase() === fullName.trim().toLowerCase() && (
              <p style={{ fontSize: 12, color: '#22c55e', marginTop: 6 }}>✓ Signature matches</p>
            )}
          </div>

          {error && (
            <div
              style={{
                background: '#1c0a0a',
                border: '1px solid #7f1d1d',
                borderRadius: 8,
                padding: '12px 16px',
                marginBottom: 20,
                fontSize: 13,
                color: '#f87171',
              }}
            >
              ⚠ {error}
            </div>
          )}

          <button
            type="submit"
            disabled={!canSubmit || submitting}
            style={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 8,
              background: canSubmit ? '#E63946' : '#1e293b',
              color: canSubmit ? '#fff' : '#4b5563',
              fontWeight: 700,
              fontSize: 15,
              padding: '14px',
              borderRadius: 10,
              border: 'none',
              cursor: canSubmit && !submitting ? 'pointer' : 'not-allowed',
              marginBottom: 16,
            }}
          >
            {submitting ? (
              'Processing…'
            ) : (
              <>
                Sign agreement & proceed to checkout <ArrowRight size={15} />
              </>
            )}
          </button>

          <p style={{ fontSize: 11, color: '#374151', textAlign: 'center', lineHeight: 1.6 }}>
            By signing you confirm all information is accurate. This agreement is logged with your
            account ID, IP address, and timestamp. Agreement version: v1.0 ·{' '}
            {new Date().toLocaleDateString('en-US', {
              year: 'numeric',
              month: 'long',
              day: 'numeric',
            })}
          </p>
        </form>
      </div>
    </div>
  )
}
